import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import type {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import type {SurveyReviewState} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {enqueueSnackbar} from "notistack";
import {RefObject, useMemo, useRef, useState} from "react";
import {useReactToPrint} from "react-to-print";
import type {SurveyAnswerContext} from "../shared/surveyAnswer.util";
import {useSurveyExportAssets} from "../shared/useSurveyExportAssets";
import {SURVEY_SUBMISSION_PRINT_STYLE} from "./export/SurveySubmissionPrintable";
import {
    buildSurveySubmissionDocumentData,
    surveySubmissionFileName,
    type SurveySubmissionDocumentData,
    type SurveySubmissionDocumentFormat
} from "./export/surveySubmissionDocument";
import type {SurveySubmissionGuest} from "./export/useSurveySubmissionGuest";

export interface SurveySubmissionDocument {
    data: SurveySubmissionDocumentData;
    printRef: RefObject<HTMLDivElement>;
    print: () => void;
    busyFormat: SurveySubmissionDocumentFormat | null;
    runExport: (format: SurveySubmissionDocumentFormat) => Promise<void>;
}

interface Args {
    /** The row as listed: the file names and the print title come from it. */
    submission: AggregationSurveySubmissionListItem;
    /** The copy on screen, so the document shows the answers the reader sees. */
    shownSubmission: AggregationSurveySubmissionListItem;
    survey?: Survey;
    ctx: SurveyAnswerContext;
    guest: SurveySubmissionGuest;
    open: boolean;
    /** Live editor state, so an unsaved review still prints what the user sees. */
    reviewState: SurveyReviewState;
    reviewNotes: string;
}

/** The one document behind Print, PDF and Word, and the actions that produce them. */
export function useSurveySubmissionDocument({
    submission,
    shownSubmission,
    survey,
    ctx,
    guest,
    open,
    reviewState,
    reviewNotes
}: Args): SurveySubmissionDocument {
    const {t} = useLocalize();
    const {organizationName, logoDataUrl, logoUrl, branding} = useSurveyExportAssets(open);
    const [busyFormat, setBusyFormat] = useState<SurveySubmissionDocumentFormat | null>(null);

    const data = useMemo(
        () => buildSurveySubmissionDocumentData({
            submission: shownSubmission,
            survey,
            ctx,
            guest,
            organizationName,
            logoDataUrl,
            logoUrl,
            branding,
            reviewState,
            reviewNotes,
        }),
        [shownSubmission, survey, ctx, guest, organizationName, logoDataUrl, logoUrl, branding, reviewState, reviewNotes]
    );

    const printRef = useRef<HTMLDivElement>(null);
    const printFn = useReactToPrint({
        contentRef: printRef,
        documentTitle: surveySubmissionFileName(submission, survey, 'pdf').replace(/\.pdf$/, ''),
        pageStyle: SURVEY_SUBMISSION_PRINT_STYLE,
    });

    const runExport = async (format: SurveySubmissionDocumentFormat) => {
        setBusyFormat(format);

        try {
            const fileName = surveySubmissionFileName(submission, survey, format);

            if (format === 'pdf') {
                const {downloadSurveySubmissionPdf} = await import('./export/SurveySubmissionPDFContent');
                await downloadSurveySubmissionPdf(data, fileName);
            } else {
                const {downloadSurveySubmissionDocument} = await import('./export/surveySubmissionDocx');
                await downloadSurveySubmissionDocument(data, fileName);
            }
        } catch (error) {
            console.log(error);
            enqueueSnackbar(t('Could not complete this action. Please try again.'), {
                variant: 'error',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            });
        } finally {
            setBusyFormat(null);
        }
    };

    return {
        data,
        printRef,
        // No argument on purpose: react-to-print would take a click event as the content to print.
        print: () => printFn(),
        busyFormat,
        runExport,
    };
}
